import React, { useState } from 'react';
import { Button, Modal, Separator, Typography, toast } from '@notch-ordering/ui-components';
import { useMutation } from '@tanstack/react-query';
import CheckIcon from '@icons/check-icon.svg';
import { FileUploadInput, FileUploadInputState } from '@v2/components/Bills/AddBillForm/FileUploadInput';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import { FETCH_DRAFT_INVOICES_QUERY_KEY } from '@ar/hooks/queries/DraftInvoicesQueries.hook';
import { UploadPdfInvoices } from '@ar/network/AccountsReceivable.network';
import { queryClient } from '@/containers/app/Root';

interface UploadPdfInvoicesModalProps {
    isOpen: boolean,
    onClose: () => void,
}

export const UploadPdfInvoicesModal: React.FC<UploadPdfInvoicesModalProps> = ({ isOpen, onClose }) => {
    const [filesState, setFilesState] = useState<FileUploadInputState>({ files: [], valid: false });
    const { supplierLoginData } = useSupplierStore();

    const uploadMutation = useMutation(
        async () => {
            if (!supplierLoginData?.supplier_id) {
                throw new Error('Supplier ID is required');
            }
            return UploadPdfInvoices({
                supplierID: supplierLoginData.supplier_id,
                invoices: filesState.files,
            });
        },
        {
            onSuccess: () => {
                toast.show({
                    message: 'Invoices uploaded successfully. They may take a few minutes to appear.',
                    icon: <CheckIcon />,
                });

                queryClient.invalidateQueries([FETCH_DRAFT_INVOICES_QUERY_KEY]);

                setFilesState({ files: [], valid: false });
                onClose();
            },
            onError: () => {
                toast.show({
                    message: 'Failed to upload invoices. Please try again.',
                });
            }
        }
    );

    const handleUpload = () => {
        if (filesState.valid && filesState.files.length > 0) {
            uploadMutation.mutate();
        }
    };

    const handleClose = () => {
        if (!uploadMutation.isLoading) {
            setFilesState({ files: [], valid: false });
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title={
                <Typography className="m-0 pr-10 mb-1" weight="font-semibold" variant="LG-2">
                    Upload Invoices
                </Typography>
            }
            headerPadding="mt-0 mb-5"
            titleSeparatorDesktop={true}
            close={handleClose}
            modalSize="SMALL"
            desktopModalWidth="lg:w-[600px]">

            {/* Scrollable content */}
            <div className="px-5 pt-5 pb-8 relative z-0 overflow-y-auto max-h-[60vh]">
                <div className="mb-4">
                    <Typography variant="BASE" className="text-gray-600">
                        Upload one or multiple PDF invoices
                    </Typography>
                </div>

                <FileUploadInput
                    files={filesState.files}
                    onChange={setFilesState}
                    allowedFileTypes={['application/pdf']}
                    multiple={true}
                    thumbnailLabel="PDF"
                    fileInputTypeDescription="Only PDF files are accepted" />
            </div>

            <Separator />

            {/* Actions - fixed at bottom */}
            <div className="px-5 py-4 flex justify-end gap-3">
                <Button
                    variant="TERTIARY_FILLED"
                    size="SMALL"
                    minWidth="w-auto"
                    type="submit"
                    onClick={handleClose}
                    disabled={uploadMutation.isLoading}>
                    Cancel
                </Button>
                <Button
                    variant={'SECONDARY'}
                    size="SMALL"
                    minWidth="w-auto"
                    type="submit"
                    onClick={handleUpload}
                    disabled={!filesState.valid || filesState.files.length === 0 || uploadMutation.isLoading}
                    loading={uploadMutation.isLoading}>
                    Upload {filesState.files.length > 0 && `(${filesState.files.length})`}
                </Button>
            </div>
        </Modal>
    );
};
